<template>
  <div class="ai-composer-wrap">
    <div v-if="showDeprecatedOverlay" class="ai-composer-wrap__overlay">
      Deprecated thread. Start a new chat to continue with the latest Tracey.
    </div>
    <div class="ai-composer" :class="{ 'ai-composer--focused': focused }" data-tutorial="composer">
      <textarea
        ref="textarea"
        :value="value"
        class="ai-composer__input"
        :placeholder="placeholder"
        rows="1"
        @input="onInput"
        @focus="focused = true"
        @blur="focused = false"
        @keydown.enter.exact.prevent="$emit('submit')"
      />
      <div v-if="outOfCredits" class="ai-composer__credits-notice">
        <v-icon x-small class="ai-composer__credits-notice-icon">mdi-information-outline</v-icon>
        <span>
          You're out of {{ traceyVersion === 'ultra' ? 'Ultra' : 'Sprint' }} credits<template v-if="resetLabel"> until {{ resetLabel }}</template>. You can
          <button type="button" class="ai-composer__credits-notice-link" @click="$emit('open-topup')">top up now</button>.
        </span>
      </div>
      <div class="ai-composer__toolbar">
        <div class="ai-composer__toolbar-left">
          <tracey-agent-select
            :value="traceyVersion"
            @input="$emit('update:tracey-version', $event)"
          />
          <v-btn
            text
            small
            class="ai-composer__library-btn"
            :color="showPromptLibrary ? 'primary' : undefined"
            @click="$emit('toggle-prompt-library')"
          >
            <v-icon left small>mdi-book-open-variant</v-icon>
            Prompts
          </v-btn>
        </div>
        <div class="ai-composer__actions">
          <v-btn
            v-if="isBusy"
            icon
            small
            class="ai-composer__stop"
            aria-label="Stop generating"
            @click="$emit('stop')"
          >
            <v-icon color="#ff1463">mdi-stop-circle</v-icon>
          </v-btn>
          <v-btn
            v-else
            icon
            small
            color="primary"
            depressed
            class="ai-composer__send"
            :disabled="!value || !value.trim() || outOfCredits"
            aria-label="Send message"
            @click="$emit('submit')"
          >
            <v-icon small>mdi-arrow-up</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
    <p class="ai-composer-wrap__hint">Tracey can make mistakes. Verify important contract search results.</p>
  </div>
</template>

<script>
import TraceyAgentSelect from './TraceyAgentSelect.vue';

export default {
  name: 'TraceyAiComposer',
  components: { TraceyAgentSelect },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: { type: String, default: '' },
    isBusy: { type: Boolean, default: false },
    showPromptLibrary: { type: Boolean, default: false },
    showDeprecatedOverlay: { type: Boolean, default: false },
    outOfCredits: { type: Boolean, default: false },
    resetLabel: { type: String, default: '' },
    traceyVersion: { type: String, default: 'ultra' },
    placeholder: {
      type: String,
      default: 'Ask about contracts, suppliers, or market insights…',
    },
  },
  data() {
    return { focused: false };
  },
  methods: {
    onInput(e) {
      this.$emit('input', e.target.value);
      this.$nextTick(this.autoGrow);
    },
    autoGrow() {
      const el = this.$refs.textarea;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    },
    focusInput() {
      this.$refs.textarea?.focus();
    },
  },
};
</script>

<style scoped>
.ai-composer-wrap {
  position: relative;
  width: 100%;
  max-width: 48rem;
  margin: 0 auto;
  padding: 20px 16px 12px;
}

.ai-composer-wrap__overlay {
  position: absolute;
  inset: 0 16px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  background: rgba(13, 13, 13, 0.85);
  color: #fff;
  border-radius: 24px;
  font-size: 14px;
}

.ai-composer__credits-notice {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0 12px 8px;
  padding: 7px 10px;
  border-radius: 8px;
  background: #f2f4f7;
  color: #667085;
  font-size: 12px;
  line-height: 1.5;
}

.ai-composer__credits-notice-icon {
  color: #98a2b3 !important;
  margin-top: 1px;
}

.ai-composer__credits-notice-link {
  font: inherit;
  color: #ff1463;
  font-weight: 600;
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.ai-composer {
  background: #fff;
  border: 1px solid #d9d9e3;
  border-radius: 24px;
  box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.ai-composer--focused {
  border-color: #acacbe;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.ai-composer__input {
  display: block;
  width: 100%;
  min-height: 24px;
  max-height: 200px;
  padding: 14px 18px 4px;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  font-family: inherit;
  font-size: 15px;
  line-height: 1.5;
  color: #0d0d0d;
}

.ai-composer__input::placeholder {
  color: #8e8ea0;
}

.ai-composer__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 10px 12px;
}

.ai-composer__toolbar-left {
  display: flex;
  align-items: center;
  gap: 2px;
}

.ai-composer__library-btn {
  text-transform: none;
  letter-spacing: 0;
  color: #6e6e80 !important;
}

.ai-composer__stop {
  background: #f7f7f8 !important;
}

.ai-composer-wrap__hint {
  margin: 8px 0 0;
  text-align: center;
  font-size: 12px;
  color: #8e8ea0;
}
</style>
